import * as Sentry from "@sentry/browser";
import type { CompetitionCatalogue } from "../types/competition";
import { catalogueBase, parseCatalogue, parseManifest } from "../types/competitionCatalogue";
import { COMPETITION_CATALOGUE_ENV } from "../utils/config";

const RETRY_DELAY = 15 * 60 * 1000;
const NETWORK_RETRY_DELAY = 1000;

/** Transport failure (offline, aborted by navigation, timeout), as opposed to a bad response. */
export class CatalogueNetworkError extends Error {
	constructor(readonly url: string, readonly cause: unknown) {
		super(`Championship catalogue network failure (${new URL(url).host}): ${String(cause)}`);
		this.name = "CatalogueNetworkError";
	}
}

// Check once per local calendar day: the first load on a new date triggers a check.
function sameLocalDate(a: number, b: number): boolean {
	return new Date(a).toDateString() === new Date(b).toDateString();
}

interface CachedCatalogue {
	catalogue: CompetitionCatalogue;
	checkedAt: number;
}

interface CatalogueDependencies {
	fetch: typeof fetch;
	storage: Pick<Storage, "getItem" | "setItem">;
	now: () => number;
	report: (error: unknown) => void;
	sleep?: (milliseconds: number) => Promise<void>;
}

export class CompetitionCatalogueRepository {
	private cached?: CachedCatalogue;
	private restored = false;
	private inFlight?: Promise<CompetitionCatalogue>;
	private retryAt = 0;

	constructor(
		private readonly dependencies: CatalogueDependencies,
		private readonly environment: "prod" | "dev",
	) {}

	private get cacheKey(): string { return `championship-catalogue-v1-${this.environment}`; }

	async load(force = false): Promise<CompetitionCatalogue> {
		if (!this.restored) {
			this.restored = true;
			try {
				const raw = this.dependencies.storage.getItem(this.cacheKey);
				if (raw) {
					const entry = JSON.parse(raw);
					if (!Number.isFinite(entry.checkedAt) || entry.checkedAt < 0 ||
						entry.checkedAt > this.dependencies.now()) throw new Error("Invalid catalogue cache timestamp");
					this.cached = { catalogue: parseCatalogue(entry.catalogue), checkedAt: entry.checkedAt };
				}
			} catch (error) {
				this.dependencies.report(error);
			}
		}
		if (this.cached) {
			if ((force || !sameLocalDate(this.cached.checkedAt, this.dependencies.now())) &&
				this.dependencies.now() >= this.retryAt) {
				void this.refresh().catch(() => {
					// refresh reports the error; the last validated catalogue remains usable.
				});
			}
			return this.cached.catalogue;
		}
		if (this.dependencies.now() < this.retryAt && !force) {
			throw new Error("Championship information is unavailable. Please try again shortly.");
		}
		return this.refresh();
	}

	private refresh(): Promise<CompetitionCatalogue> {
		if (this.inFlight) return this.inFlight;
		this.inFlight = this.download().catch((error: unknown) => {
			this.retryAt = this.dependencies.now() + RETRY_DELAY;
			this.dependencies.report(error);
			throw error;
		}).finally(() => { this.inFlight = undefined; });
		return this.inFlight;
	}

	private async request(url: string): Promise<Response> {
		for (let attempt = 0; ; attempt++) {
			try {
				return await this.dependencies.fetch(url, {
					credentials: "omit",
					signal: AbortSignal.timeout(8000),
				});
			} catch (error) {
				if (attempt > 0) throw new CatalogueNetworkError(url, error);
				await (this.dependencies.sleep ?? sleep)(NETWORK_RETRY_DELAY);
			}
		}
	}

	private async json(url: string): Promise<unknown> {
		const response = await this.request(url);
		if (!response.ok) throw new Error(`Catalogue request failed: ${response.status}`);
		return response.json();
	}

	private async download(): Promise<CompetitionCatalogue> {
		const base = catalogueBase(this.environment);
		const manifest = parseManifest(await this.json(`${base}current.json`), base);
		const catalogue = this.cached?.catalogue.revision === manifest.revision
			? this.cached.catalogue
			: parseCatalogue(await this.json(manifest.url));
		if (catalogue.revision !== manifest.revision) throw new Error("Catalogue revision mismatch");
		this.cached = { catalogue, checkedAt: this.dependencies.now() };
		this.retryAt = 0;
		try {
			this.dependencies.storage.setItem(this.cacheKey, JSON.stringify(this.cached));
		} catch (error) {
			this.dependencies.report(error);
		}
		return catalogue;
	}
}

function sleep(milliseconds: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

let repository: CompetitionCatalogueRepository | undefined;
let unloading = false;

export class CatalogueUnavailableError extends Error {
	constructor() {
		super("Championship information is unavailable. Please try again.");
		this.name = "CatalogueUnavailableError";
	}
}

function reportCatalogueError(error: unknown): void {
	if (error instanceof CatalogueNetworkError) {
		// Navigation cancels in-flight requests; those are not failures.
		if (unloading) return;
		console.warn("Championship catalogue:", error);
		Sentry.captureException(error, {
			level: "warning",
			fingerprint: ["championship-catalogue-network"],
		});
		return;
	}
	console.error("Championship catalogue:", error);
	Sentry.captureException(error);
}

export function loadCompetitionCatalogue(force = false): Promise<CompetitionCatalogue> {
	if (!repository) {
		window.addEventListener("pagehide", () => { unloading = true; });
		window.addEventListener("pageshow", () => { unloading = false; });
	}
	repository ??= new CompetitionCatalogueRepository({
		fetch: (input, init) => window.fetch(input, init),
		storage: {
			getItem: (key) => window.localStorage.getItem(key),
			setItem: (key, value) => window.localStorage.setItem(key, value),
		},
		now: Date.now,
		report: reportCatalogueError,
	}, COMPETITION_CATALOGUE_ENV === "prod" ? "prod" : "dev");
	return repository.load(force).catch(() => {
		// The underlying failure has already been reported by the repository.
		throw new CatalogueUnavailableError();
	});
}
